import { approvalRequestApi } from "@/api/approval/approval-request.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { employeeTodayKeys } from "@/queries/employees/use-employee-today.query.ts";
import { ApiErrorException, ApiPassException } from "@/types/api.types.ts";
import {
  ApiApprovalRequestApprovePayload,
  ApiApprovalRequestConfirmationCommentUpdate,
  ApiApprovalRequestMyFilter,
} from "@/types/approvals/approval.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const approvalRequestsKeys = createQueryKeys("approvalRequests", {
  my: (filter: ApiApprovalRequestMyFilter) => ({
    queryKey: [filter],
    queryFn: async ({ signal }) => {
      const result = await approvalRequestApi.searchMy(filter, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  details: (id: number | undefined) => ({
    queryKey: [id],
    queryFn: async () => {
      if (id == undefined) throw new ApiPassException("Approval request id is required");

      const result = await approvalRequestApi.getById(id);

      if (result.error) throw new ApiErrorException(result.error);

      return result.data;
    },
  }),
});

export const useMyApprovalRequests = (filter: ApiApprovalRequestMyFilter) => {
  return useQuery(approvalRequestsKeys.my(filter));
};

export const useApprovalRequestDetails = (id: number | undefined, enabled: boolean = true) => {
  return useQuery({
    ...approvalRequestsKeys.details(id),
    enabled: enabled && !!id,
  });
};

export const useApprovalRequestApprove = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number;
      payload: ApiApprovalRequestApprovePayload;
    }) => {
      const result = await approvalRequestApi.approve(id, payload);
      if (result.error) throw new ApiErrorException(result.error);
      return id;
    },
    onSuccess: (id) => {
      void queryClient.invalidateQueries({
        queryKey: approvalRequestsKeys.details(id).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: employeeTodayKeys.getToday._def,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useApprovalRequestReject = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number;
      payload: ApiApprovalRequestApprovePayload;
    }) => {
      const result = await approvalRequestApi.reject(id, payload);
      if (result.error) throw new ApiErrorException(result.error);
      return id;
    },
    onSuccess: (id) => {
      void queryClient.invalidateQueries({
        queryKey: approvalRequestsKeys.details(id).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: employeeTodayKeys.getToday._def,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useApprovalRequestCancel = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await approvalRequestApi.cancel(id);
      if (result.error) throw new ApiErrorException(result.error);
      return id;
    },
    onSuccess: (id) => {
      void queryClient.invalidateQueries({
        queryKey: approvalRequestsKeys.details(id).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: employeeTodayKeys.getToday._def,
      });
      void queryClient.invalidateQueries({
        queryKey: approvalRequestsKeys.my._def,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useApprovalRequestConfirmationCommentUpdate = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number;
      payload: ApiApprovalRequestConfirmationCommentUpdate;
    }) => {
      const result = await approvalRequestApi.updateConfirmationComment(id, payload);
      if (result.error) throw new ApiErrorException(result.error);
      return id;
    },
    onSuccess: (id) => {
      void queryClient.invalidateQueries({
        queryKey: approvalRequestsKeys.details(id).queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
